import React, { createContext, useState, useEffect, useMemo } from "react";
import { TOUR_CONFIGS } from "@/config/tourConfig";
import { useI18nNavigate } from "@/hooks/useI18nNavigation";

// Tour Types
export type TourType = "create-circle" | "circles-page";

// Tour Configuration
export interface TourConfig {
  id: TourType;
  name: string;
  totalSteps: number;
  storageKey: string;
  description: string;
}

interface TourState {
  isActive: boolean;
  currentStep: number;
  totalSteps: number;
  tourType: TourType | null;
  realCircleId: string | null; // Add real circle ID for interactive tours
  isCompleted: boolean; // Track if tour was completed
  isSkipped: boolean; // Track if tour was skipped
  mockData: {
    circleTitle: string;
    circleDescription: string;
    circleImage: File | null;
  };
  formValidation: {
    isFormValid: boolean;
    titleValid: boolean;
    descriptionValid: boolean;
  };
}

export interface TourContextType {
  tourState: TourState;
  availableTours: TourConfig[];
  currentTourConfig: TourConfig | null;
  startTour: (tourType?: TourType, customTotalSteps?: number) => void;
  stopTour: () => void;
  completeTour: () => void;
  skipTour: () => void;
  goToStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  setMockData: (data: Partial<TourState["mockData"]>) => void;
  setRealCircleId: (id: string) => void;
  setFormValidation: (validation: Partial<TourState["formValidation"]>) => void;
  isTourCompletedOrSkipped: (tourType: TourType) => boolean;
  getTourStatus: (tourType: TourType) => "completed" | "skipped" | "incomplete" | "not-started";
  restoreTourIfIncomplete: (tourType: TourType) => boolean;
}

export const TourContext = createContext<TourContextType | undefined>(undefined);

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useI18nNavigate();

  const [tourState, setTourState] = useState<TourState>(() => {
    return {
      isActive: false,
      currentStep: 0,
      totalSteps: 0,
      tourType: null,
      realCircleId: null,
      isCompleted: false,
      isSkipped: false,
      mockData: {
        circleTitle: "",
        circleDescription: "",
        circleImage: null,
      },
      formValidation: {
        isFormValid: false,
        titleValid: false,
        descriptionValid: false,
      },
    };
  });

  const availableTours = Object.values(TOUR_CONFIGS);
  const currentTourConfig = tourState.tourType ? TOUR_CONFIGS[tourState.tourType] : null;

  // Save tour state to localStorage
  useEffect(() => {
    if (tourState.isActive && tourState.tourType) {
      const config = TOUR_CONFIGS[tourState.tourType];
      localStorage.setItem(
        config.storageKey,
        JSON.stringify({
          isActive: tourState.isActive,
          currentStep: tourState.currentStep,
          totalSteps: tourState.totalSteps,
          tourType: tourState.tourType,
          isCompleted: tourState.isCompleted,
          isSkipped: tourState.isSkipped,
        }),
      );
    }
  }, [
    tourState.isActive,
    tourState.currentStep,
    tourState.tourType,
    tourState.isCompleted,
    tourState.isSkipped,
  ]);

  const startTour = (tourType: TourType = "create-circle", customTotalSteps?: number) => {
    const config = TOUR_CONFIGS[tourType];

    // remove any existing tour data from storage
    localStorage.removeItem(config.storageKey);

    setTourState((prev) => ({
      ...prev,
      isActive: true,
      currentStep: 0,
      totalSteps: customTotalSteps || config.totalSteps,
      tourType,
    }));
  };

  const stopTour = () => {
    // Clear storage for current tour type
    if (tourState.tourType) {
      const config = TOUR_CONFIGS[tourState.tourType];
      localStorage.removeItem(config.storageKey);
    }

    setTourState({
      isActive: false,
      currentStep: 0,
      totalSteps: 0,
      tourType: null,
      realCircleId: null,
      isCompleted: false,
      isSkipped: false,
      mockData: {
        circleTitle: "",
        circleDescription: "",
        circleImage: null,
      },
      formValidation: {
        isFormValid: false,
        titleValid: false,
        descriptionValid: false,
      },
    });
  };

  const completeTour = () => {
    if (tourState.tourType) {
      const config = TOUR_CONFIGS[tourState.tourType];
      // Save tour as completed to localStorage
      localStorage.setItem(
        config.storageKey,
        JSON.stringify({
          isActive: false,
          currentStep: tourState.totalSteps - 1,
          totalSteps: tourState.totalSteps,
          tourType: tourState.tourType,
          isCompleted: true,
          isSkipped: false,
        }),
      );

      // Update state to mark as completed
      setTourState((prev) => ({
        ...prev,
        isActive: false,
        isCompleted: true,
        isSkipped: false,
      }));
    }
  };

  const skipTour = () => {
    if (tourState.tourType) {
      const config = TOUR_CONFIGS[tourState.tourType];
      // Save tour as skipped to localStorage
      localStorage.setItem(
        config.storageKey,
        JSON.stringify({
          isActive: false,
          currentStep: tourState.currentStep,
          totalSteps: tourState.totalSteps,
          tourType: tourState.tourType,
          isCompleted: false,
          isSkipped: true,
        }),
      );

      // Update state to mark as skipped
      setTourState((prev) => ({
        ...prev,
        isActive: false,
        isCompleted: false,
        isSkipped: true,
      }));
    }
  };

  const isTourCompletedOrSkipped = (tourType: TourType): boolean => {
    const config = TOUR_CONFIGS[tourType];
    const saved = localStorage.getItem(config.storageKey);
    if (saved) {
      try {
        const parsedState = JSON.parse(saved);
        return parsedState.isCompleted === true || parsedState.isSkipped === true;
      } catch {
        return false;
      }
    }
    return false;
  };

  const getTourStatus = (
    tourType: TourType,
  ): "completed" | "skipped" | "incomplete" | "not-started" => {
    const config = TOUR_CONFIGS[tourType];
    const saved = localStorage.getItem(config.storageKey);

    if (!saved) {
      return "not-started";
    }

    try {
      const parsedState = JSON.parse(saved);

      if (parsedState.isCompleted) {
        return "completed";
      }

      if (parsedState.isSkipped) {
        return "skipped";
      }

      if (parsedState.isActive) {
        return "incomplete";
      }

      return "not-started";
    } catch {
      localStorage.removeItem(config.storageKey);
      return "not-started";
    }
  };

  const restoreTourIfIncomplete = (tourType: TourType): boolean => {
    const status = getTourStatus(tourType);

    if (status === "incomplete") {
      const config = TOUR_CONFIGS[tourType];
      const saved = localStorage.getItem(config.storageKey);
      if (saved) {
        try {
          const parsedState = JSON.parse(saved);
          setTourState((prev) => ({
            ...prev,
            isActive: parsedState.isActive,
            currentStep: parsedState.currentStep,
            totalSteps: parsedState.totalSteps,
            tourType: parsedState.tourType,
            isCompleted: parsedState.isCompleted || false,
            isSkipped: parsedState.isSkipped || false,
          }));
          if (tourType === "create-circle") {
            // Redirect to create circle page based on current step
            redirectCreateCircleBasedOnStep(parsedState.currentStep);
          } else if (tourType === "circles-page") {
            // Redirect to circles page based on current step
            redirectCirclesPageBasedOnStep(parsedState.currentStep);
          }
          return true;
        } catch {
          localStorage.removeItem(config.storageKey);
        }
      }
    }

    return false;
  };

  const goToStep = (step: number) => {
    if (!tourState.tourType || step < 0 || step >= tourState.totalSteps) {
      return;
    }
    setTourState((prev) => ({
      ...prev,
      currentStep: step,
    }));

    if (tourState.tourType === "create-circle") {
      // Redirect based on current step
      redirectCreateCircleBasedOnStep(step);
    } else if (tourState.tourType === "circles-page") {
      // For circles page, just update current step
      redirectCirclesPageBasedOnStep(step);
    }
  };

  const redirectCirclesPageBasedOnStep = (step: number) => {
    if (step >= 1) {
      navigate({ to: "/donor-profile", search: { page: "circles" } });
    }
  };

  const redirectCreateCircleBasedOnStep = (step: number) => {
    // Navigation logic based on tour type
    // Existing create circle navigation logic
    if (step <= 1) {
      navigate({ to: "/coh" });
    } else if (step === 2) {
      navigate({ to: "/circle-creation" });
    } else if (step >= 4) {
      // Use real circle ID if available
      const circleId = tourState.realCircleId;
      if (!circleId) {
        return;
      }
      navigate({ to: `/circle/$circleId`, params: { circleId } });
    }
  };

  const nextStep = () => {
    const nextStepIndex = tourState.currentStep + 1;
    if (nextStepIndex === tourState.totalSteps) {
      completeTour();
      return;
    }
    if (nextStepIndex > tourState.totalSteps) {
      stopTour();
      return;
    }
    goToStep(nextStepIndex);
  };

  const previousStep = () => {
    const prevStepIndex = tourState.currentStep - 1;
    if (prevStepIndex < 0) {
      return;
    }
    goToStep(prevStepIndex);
  };

  const setMockData = (data: Partial<TourState["mockData"]>) => {
    setTourState((prev) => ({
      ...prev,
      mockData: { ...prev.mockData, ...data },
    }));
  };

  const setRealCircleId = (id: string) => {
    setTourState((prev) => ({ ...prev, realCircleId: id }));
  };

  const setFormValidation = (validation: Partial<TourState["formValidation"]>) => {
    setTourState((prev) => ({
      ...prev,
      formValidation: { ...prev.formValidation, ...validation },
    }));
  };

  const contextValue = useMemo(
    () => ({
      tourState,
      availableTours,
      currentTourConfig,
      startTour,
      stopTour,
      completeTour,
      skipTour,
      goToStep,
      nextStep,
      previousStep,
      setMockData,
      setRealCircleId,
      setFormValidation,
      isTourCompletedOrSkipped,
      getTourStatus,
      restoreTourIfIncomplete,
    }),
    [
      tourState.isActive,
      tourState.currentStep,
      tourState.totalSteps,
      tourState.tourType,
      tourState.realCircleId,
      tourState.formValidation.isFormValid,
      tourState.isCompleted,
      tourState.isSkipped,
    ],
  );

  return <TourContext.Provider value={contextValue}>{children}</TourContext.Provider>;
};
